import React, { useState, useEffect } from 'react';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';
import { Search, Calendar, User as UserIcon, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { fetchArticles } from '../lib/api';

const categories = ['Tous', 'Environnement', 'Santé', 'Pratique', 'Technique', 'International'];

export default function Blog() {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('Tous');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat !== 'Tous') params.category = cat;
    if (search) params.search = search;
    const t = setTimeout(() => {
      fetchArticles(params)
        .then((data) => setArticles(data || []))
        .catch(() => setArticles([]))
        .finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [cat, search]);

  return (
    <>
      <section className="pt-32 pb-12 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">Documentation</span>
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              Le <span className="gradient-text">blog</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">
              Articles de fond et de réflexion sur l'eau comme boisson et sur les solutions pour éviter les bouteilles plastiques d'eau.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-10 bg-white sticky top-20 z-30 border-b border-sky-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            <div className="relative max-w-md w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un article..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 h-12 rounded-2xl border-sky-200 focus-visible:ring-sky-400"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    cat === c ? 'bg-sky-900 text-white' : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-sky-50/50 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
            </div>
          ) : articles.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Aucun article ne correspond à votre recherche.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((p, i) => (
                <Reveal key={p.id} delay={Math.min(i, 2) * 70}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full">
                    <article className="h-full flex flex-col rounded-3xl overflow-hidden bg-white border border-sky-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className="aspect-[16/10] overflow-hidden bg-sky-50">
                        {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                          <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold">{p.category}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.readTime}</span>
                        </div>
                        <h2 className="font-display font-bold text-lg text-sky-950 mb-2 group-hover:text-sky-700 transition-colors leading-snug">{p.title}</h2>
                        <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>
                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-sky-50 text-xs text-slate-500">
                          <span className="flex items-center gap-1.5"><UserIcon className="w-3 h-3" />{p.author}</span>
                          <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{p.date}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sky-700 font-semibold text-sm mt-4 group-hover:gap-2.5 transition-all">
                          Lire l'article<ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </article>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
